/**
 * MediaAlbum/MediaGalleryPublic.jsx
 * ════════════════════════════════════════════════════════════════
 * Публічна галерея садочка — два стани через useState:
 *   1. AlbumList  — сітка альбомів
 *   2. AlbumView  — відкритий альбом з лайтбоксом
 *
 * Props:
 *   kgId      {string}
 *   groups    {array}   [{ _id, name }]
 *   groupId   {string?} — якщо передано, показуємо тільки альбоми
 *                         де group_ids містить саме цей id
 *                         (порожній group_ids = "всі групи" — не показуємо)
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faChevronLeft, faChevronRight,
    faX, faExpand, faImage, faCalendarDays,
    faPlay,
} from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { constr_uri } from '../../../../../services/apiClient';
import { listPublicAlbums, getPublicAlbum } from './actions';

// ─── Константи ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 12;

const CATEGORY_META = {
    event:    { label: 'Свято / захід', bg: '#e6f1fb', color: '#0c447c' },
    sport:    { label: 'Спорт',         bg: '#e1f5ee', color: '#085041' },
    creative: { label: 'Творчість',     bg: '#fbeaf0', color: '#72243e' },
    room:     { label: 'Приміщення',    bg: '#faeeda', color: '#633806' },
    walk:     { label: 'Прогулянка',    bg: '#eaf3de', color: '#27500a' },
    other:    { label: 'Інше',          bg: '#f1efe8', color: '#444441' },
};

const formatDate = function(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('uk-UA', {
        day: 'numeric', month: 'long', year: 'numeric'
    });
};

const formatDateShort = function(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('uk-UA', {
        day: 'numeric', month: 'short', year: 'numeric'
    });
};

// Витягти YouTube videoId з URL
const getYoutubeId = function(url) {
    if (!url) return null;
    var match = url.match(/(?:v=|youtu\.be\/)([^?&#]+)/);
    return match ? match[1] : null;
};


// ══════════════════════════════════════════════════════════════════════════════
// CSS анімації — вставляємо один раз в DOM
// ══════════════════════════════════════════════════════════════════════════════

var LIGHTBOX_STYLES_INJECTED = false;

function injectLightboxStyles() {
    if (LIGHTBOX_STYLES_INJECTED) return;
    LIGHTBOX_STYLES_INJECTED = true;
    var style = document.createElement('style');
    style.textContent = [
        '@keyframes lb-slide-left  { from { opacity:0; transform:translateX(60px)  } to { opacity:1; transform:translateX(0) } }',
        '@keyframes lb-slide-right { from { opacity:0; transform:translateX(-60px) } to { opacity:1; transform:translateX(0) } }',
        '@keyframes lb-fade        { from { opacity:0; transform:scale(0.97)       } to { opacity:1; transform:scale(1)     } }',
        '@keyframes lb-progress    { from { width:0% } to { width:100% } }',
        '.lb-media-enter-left  { animation: lb-slide-left  0.35s cubic-bezier(.25,.46,.45,.94) both }',
        '.lb-media-enter-right { animation: lb-slide-right 0.35s cubic-bezier(.25,.46,.45,.94) both }',
        '.lb-media-enter-fade  { animation: lb-fade        0.3s  ease both }',
    ].join('\n');
    document.head.appendChild(style);
}

// ══════════════════════════════════════════════════════════════════════════════
// ЛАЙТБОКС зі slideshow
// ══════════════════════════════════════════════════════════════════════════════

var SLIDESHOW_DELAY = 4000; // мс на одне фото

// ── Індикатор точок ──────────────────────────────────────────────────────────
//
// N точок, "вікно яскравості" з 5 точок плавно рухається зліва направо.
// Центр вікна = progress (0..1) * N
// Яскравість кожної точки = gaussian від відстані до центру вікна.

var DOTS_COUNT  = 20;
var WINDOW_SIZE = 5;    // ширина вікна (в точках)
var SIGMA       = 1.8;  // "м'якість" gaussian

function dotOpacity(dotIndex, progress) {
    var center = progress * (DOTS_COUNT - 1);
    var dist   = dotIndex - center;
    // gaussian: e^(-dist²/(2σ²))
    var g = Math.exp(-(dist * dist) / (2 * SIGMA * SIGMA));
    // базова яскравість 0.18, максимальна 1.0
    return 0.18 + g * 0.82;
}

function DotsIndicator(props) {
    // progress 0..100 → 0..1
    var p = props.progress / 100;
    return (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: 'center' }}>
            {Array.from({ length: DOTS_COUNT }, function(_, i) {
                var opacity = dotOpacity(i, p);
                var scale   = 0.7 + opacity * 0.5;
                return (
                    <div
                        key={i}
                        style={{
                            width: 7, height: 7,
                            borderRadius: '50%',
                            background: '#4d9fff',
                            opacity: opacity,
                            transform: 'scale(' + scale + ')',
                            transition: 'opacity 0.05s, transform 0.05s',
                            flexShrink: 0,
                        }}
                    />
                );
            })}
        </div>
    );
}

const Lightbox = function({ items, startIndex, docId, onClose }) {
    const [current, setCurrent]         = useState(startIndex || 0);
    const [direction, setDirection]     = useState('left');
    const [animKey, setAnimKey]         = useState(0);
    const [imgLoaded, setImgLoaded]     = useState(false);
    const [isPlaying, setIsPlaying]     = useState(true);   // ← автостарт
    const [progress, setProgress]       = useState(0);
    const iframeRef                     = useRef(null);
    const slideshowRef                  = useRef(null);
    const progressRef                   = useRef(null);
    const touchStartX                   = useRef(null);
    const touchStartY                   = useRef(null);

    useEffect(function() { injectLightboxStyles(); }, []);

    var item      = items[current];
    var isYoutube = item && item.source === 'youtube';
    var youtubeId = isYoutube ? getYoutubeId(item.youtube_url) : null;

    // ── Зупинити iframe відео ────────────────────────────────────────────────
    var stopVideo = function() {
        if (iframeRef.current) {
            // Перевантажуємо src — найнадійніший спосіб зупинити YouTube
            var src = iframeRef.current.src;
            iframeRef.current.src = '';
            iframeRef.current.src = src;
        }
    };

    // ── Навігація ────────────────────────────────────────────────────────────
    var goTo = function(idx, dir) {
        stopVideo();
        setImgLoaded(false);
        setDirection(dir || 'fade');
        setAnimKey(function(k) { return k + 1; });
        setCurrent(idx);
        setProgress(0);
    };

    var prev = function() {
        var idx = current > 0 ? current - 1 : items.length - 1;
        goTo(idx, 'right');
    };

    var next = function() {
        var idx = current < items.length - 1 ? current + 1 : 0;
        goTo(idx, 'left');
    };

    // ── Slideshow ────────────────────────────────────────────────────────────
    // Пауза автоматично якщо поточний елемент — відео
    var effectivePlaying = isPlaying && !isYoutube;

    useEffect(function() {
        if (!effectivePlaying) {
            clearInterval(slideshowRef.current);
            clearInterval(progressRef.current);
            return;
        }

        setProgress(0);
        var start = Date.now();

        // Прогрес-бар оновлюємо кожні 50мс
        progressRef.current = setInterval(function() {
            var elapsed = Date.now() - start;
            var pct = Math.min(100, (elapsed / SLIDESHOW_DELAY) * 100);
            setProgress(pct);
        }, 50);

        slideshowRef.current = setTimeout(function() {
            next();
        }, SLIDESHOW_DELAY);

        return function() {
            clearTimeout(slideshowRef.current);
            clearInterval(progressRef.current);
        };
    }, [effectivePlaying, current]);

    var togglePlay = function() {
        setIsPlaying(function(p) { return !p; });
        setProgress(0);
    };

    // ── Клавіатура ───────────────────────────────────────────────────────────
    useEffect(function() {
        var handler = function(e) {
            if (e.key === 'ArrowRight')      next();
            else if (e.key === 'ArrowLeft')  prev();
            else if (e.key === 'Escape')     onClose();
            else if (e.key === ' ') {
                e.preventDefault(); // не скролити сторінку
                togglePlay();
            }
        };
        document.addEventListener('keydown', handler);
        return function() { document.removeEventListener('keydown', handler); };
    }, [current, isPlaying]);

    // ── Блокуємо скрол body ──────────────────────────────────────────────────
    useEffect(function() {
        document.body.style.overflow = 'hidden';
        return function() { document.body.style.overflow = ''; };
    }, []);

    // ── Swipe ────────────────────────────────────────────────────────────────
    var handleTouchStart = function(e) {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    };
    var handleTouchEnd = function(e) {
        if (touchStartX.current === null) return;
        var dx = e.changedTouches[0].clientX - touchStartX.current;
        var dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
        if (Math.abs(dx) > 50 && dy < 60) {
            if (dx > 0) prev(); else next();
        }
        touchStartX.current = null;
    };

    // ── Клас анімації ────────────────────────────────────────────────────────
    var animClass = direction === 'left'
        ? 'lb-media-enter-left'
        : direction === 'right'
            ? 'lb-media-enter-right'
            : 'lb-media-enter-fade';

    if (!item) return null;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: '#000',
                display: 'flex', flexDirection: 'column',
                userSelect: 'none',
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {/* ── Індикатор точок ── */}
            {items.length > 1 && (
                <div style={{
                    padding: '8px 20px 6px',
                    flexShrink: 0,
                    background: 'rgba(0,0,0,0.5)',
                }}>
                    <DotsIndicator progress={effectivePlaying ? progress : 0} />
                </div>
            )}

            {/* ── Шапка ── */}
            <div style={{
                display: 'flex', alignItems: 'center',
                padding: '10px 16px', flexShrink: 0,
                background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
                gap: 10,
            }}>
                {/* Назва і лічильник */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {item.caption && (
                        <div style={{
                            fontSize: 15, fontWeight: 500, color: '#fff',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            marginBottom: 2, textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                        }}>
                            {item.caption}
                        </div>
                    )}
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                        {current + 1} / {items.length}
                        {isYoutube && (
                            <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <FontAwesomeIcon icon={faYoutube} style={{ color: '#ff4444' }} />
                                YouTube
                            </span>
                        )}
                    </div>
                </div>

                {/* Кнопки керування */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {/* Slideshow play/pause — тільки якщо > 1 і не відео */}
                    {items.length > 1 && (
                        <CtrlBtn
                            onClick={togglePlay}
                            title={isPlaying ? 'Пауза (Пробіл)' : 'Слайдшоу (Пробіл)'}
                            active={isPlaying}
                        >
                            {isPlaying
                                ? <PauseIcon />
                                : <FontAwesomeIcon icon={faPlay} style={{ fontSize: 13, marginLeft: 2 }} />}
                            {isYoutube && isPlaying && (
                                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginLeft: 4 }}>
                                    пауза на відео
                                </span>
                            )}
                        </CtrlBtn>
                    )}
                    {/* Закрити */}
                    <CtrlBtn onClick={onClose} title="Закрити (Esc)">
                        <FontAwesomeIcon icon={faX} style={{ fontSize: 14 }} />
                    </CtrlBtn>
                </div>
            </div>

            {/* ── Медіа ── */}
            <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative', overflow: 'hidden',
            }}>
                {/* Стрілка ліво */}
                {items.length > 1 && (
                    <NavArrow dir="left" onClick={prev} />
                )}

                {/* Вміст з анімацією */}
                <div
                    key={animKey}
                    className={animClass}
                    style={{ maxWidth: '90vw', maxHeight: '82vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    {isYoutube && youtubeId ? (
                        <div style={{ width: 'min(880px, 90vw)', aspectRatio: '16/9' }}>
                            <iframe
                                ref={iframeRef}
                                src={'https://www.youtube.com/embed/' + youtubeId + '?autoplay=1&rel=0&modestbranding=1&enablejsapi=1'}
                                style={{ width: '100%', height: '100%', border: 'none', borderRadius: 10 }}
                                allow="autoplay; encrypted-media; fullscreen"
                                allowFullScreen
                                title={item.caption || 'Відео'}
                            />
                        </div>
                    ) : (
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {!imgLoaded && (
                                <div style={{
                                    position: 'absolute', inset: 0, minWidth: 200, minHeight: 150,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Spinner animation="border" variant="light" size="sm" />
                                </div>
                            )}
                            <img
                                key={item.id}
                                src={constr_uri('docfile?doc_id=' + docId + '&file_id=' + item.id)}
                                alt={item.caption || ''}
                                onLoad={function() { setImgLoaded(true); }}
                                style={{
                                    maxWidth: '88vw', maxHeight: '80vh',
                                    objectFit: 'contain', borderRadius: 6,
                                    opacity: imgLoaded ? 1 : 0,
                                    transition: 'opacity 0.25s',
                                    display: 'block',
                                    boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Стрілка право */}
                {items.length > 1 && (
                    <NavArrow dir="right" onClick={next} />
                )}

                {/* Підказка пробіл — показуємо 3 сек після відкриття */}
                {items.length > 1 && (
                    <HintBadge />
                )}
            </div>

            {/* ── Стрічка мініатюр ── */}
            {items.length > 1 && (
                <ThumbnailStrip
                    items={items}
                    current={current}
                    docId={docId}
                    onSelect={function(idx) {
                        var dir = idx > current ? 'left' : 'right';
                        goTo(idx, dir);
                    }}
                />
            )}
        </div>
    );
};

// ── Допоміжні компоненти лайтбоксу ──────────────────────────────────────────

function CtrlBtn(props) {
    return (
        <button
            onClick={props.onClick}
            title={props.title}
            style={{
                background: props.active ? 'rgba(77,159,255,0.3)' : 'rgba(255,255,255,0.12)',
                border: '1px solid ' + (props.active ? 'rgba(77,159,255,0.5)' : 'rgba(255,255,255,0.2)'),
                borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
                color: '#fff', display: 'flex', alignItems: 'center', gap: 5,
                transition: 'background 0.15s',
                minWidth: 36, height: 34, justifyContent: 'center',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = props.active ? 'rgba(77,159,255,0.45)' : 'rgba(255,255,255,0.22)'; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = props.active ? 'rgba(77,159,255,0.3)' : 'rgba(255,255,255,0.12)'; }}
        >
            {props.children}
        </button>
    );
}

function PauseIcon() {
    return (
        <svg width="13" height="14" viewBox="0 0 13 14" fill="none">
            <rect x="0" y="0" width="4" height="14" rx="1.5" fill="white" />
            <rect x="8" y="0" width="4" height="14" rx="1.5" fill="white" />
        </svg>
    );
}

function NavArrow(props) {
    var isLeft = props.dir === 'left';
    return (
        <button
            onClick={props.onClick}
            style={{
                position: 'absolute',
                left: isLeft ? 12 : 'auto',
                right: isLeft ? 'auto' : 12,
                zIndex: 10,
                width: 48, height: 48, borderRadius: '50%',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.2)',
                cursor: 'pointer', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s, transform 0.15s',
            }}
            onMouseEnter={function(e) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                e.currentTarget.style.transform = 'scale(1.08)';
            }}
            onMouseLeave={function(e) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                e.currentTarget.style.transform = 'scale(1)';
            }}
        >
            <FontAwesomeIcon icon={isLeft ? faChevronLeft : faChevronRight} style={{ fontSize: 18 }} />
        </button>
    );
}

function HintBadge() {
    const [visible, setVisible] = useState(true);
    useEffect(function() {
        var t = setTimeout(function() { setVisible(false); }, 3000);
        return function() { clearTimeout(t); };
    }, []);
    if (!visible) return null;
    return (
        <div style={{
            position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.6)', borderRadius: 20,
            padding: '5px 14px', fontSize: 12, color: 'rgba(255,255,255,0.7)',
            pointerEvents: 'none', whiteSpace: 'nowrap',
            animation: 'lb-fade 0.4s ease both',
        }}>
            ← → навігація &nbsp;·&nbsp; Пробіл — слайдшоу &nbsp;·&nbsp; Esc — закрити
        </div>
    );
}

function ThumbnailStrip(props) {
    var stripRef = useRef(null);

    // Автоскрол до активної мініатюри
    useEffect(function() {
        if (!stripRef.current) return;
        var el = stripRef.current.children[props.current];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [props.current]);

    return (
        <div
            ref={stripRef}
            style={{
                display: 'flex', gap: 5, padding: '10px 14px',
                overflowX: 'auto', flexShrink: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                scrollbarWidth: 'none',
            }}
        >
            {props.items.map(function(it, idx) {
                var isYt  = it.source === 'youtube';
                var isAct = idx === props.current;
                return (
                    <div
                        key={it.id}
                        onClick={function() { props.onSelect(idx); }}
                        style={{
                            width: 56, height: 40, flexShrink: 0,
                            borderRadius: 6, overflow: 'hidden',
                            cursor: 'pointer', position: 'relative',
                            border: isAct ? '2px solid #4d9fff' : '2px solid transparent',
                            opacity: isAct ? 1 : 0.5,
                            transition: 'opacity 0.2s, border-color 0.2s, transform 0.15s',
                            transform: isAct ? 'scale(1.08)' : 'scale(1)',
                            background: '#111',
                        }}
                        onMouseEnter={function(e) { if (!isAct) e.currentTarget.style.opacity = '0.8'; }}
                        onMouseLeave={function(e) { if (!isAct) e.currentTarget.style.opacity = '0.5'; }}
                    >
                        {isYt ? (
                            <>
                                {it.thumbnail_url && (
                                    <img src={it.thumbnail_url} alt=""
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                )}
                                <div style={{
                                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <FontAwesomeIcon icon={faPlay} style={{ fontSize: 9, color: '#fff' }} />
                                </div>
                            </>
                        ) : (
                            <img
                                src={constr_uri('docfile?doc_id=' + props.docId + '&file_id=' + it.id)}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                loading="lazy"
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}


// ══════════════════════════════════════════════════════════════════════════════
// СТОРІНКА АЛЬБОМУ
// ══════════════════════════════════════════════════════════════════════════════

const PHOTO_PAGE_SIZE = 24;

const AlbumView = function({ albumId, onBack }) {
    const [album, setAlbum]       = useState(null);
    const [loading, setLoading]   = useState(true);
    const [page, setPage]         = useState(1);
    const [lightbox, setLightbox] = useState(null);

    // Refs на всі iframe відео на сторінці
    const videoRefsMap = useRef({});

    // Зупинити всі відео на сторінці
    var stopAllVideos = function() {
        Object.values(videoRefsMap.current).forEach(function(iframe) {
            if (!iframe) return;
            var src = iframe.src;
            iframe.src = '';
            iframe.src = src;
        });
    };

    useEffect(function() {
        setLoading(true);
        getPublicAlbum(albumId)
            .then(function(data) { setAlbum(data); })
            .catch(function() {})
            .finally(function() { setLoading(false); });
    }, [albumId]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!album) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#adb5bd' }}>
                Альбом не знайдено
            </div>
        );
    }

    var cat       = CATEGORY_META[album.category] || null;
    var allFiles  = album.files || [];
    var photos    = allFiles.filter(function(f) { return f.source !== 'youtube'; });
    var videos    = allFiles.filter(function(f) { return f.source === 'youtube'; });

    var totalPages  = Math.ceil(photos.length / PHOTO_PAGE_SIZE);
    var pagedPhotos = photos.slice((page - 1) * PHOTO_PAGE_SIZE, page * PHOTO_PAGE_SIZE);
    var lightboxItems = pagedPhotos.concat(videos);

    var openLightbox = function(index) {
        stopAllVideos(); // ← зупиняємо відео перед відкриттям лайтбоксу
        setLightbox({ items: lightboxItems, startIndex: index });
    };

    return (
        <div>
            {/* Кнопка назад */}
            <div style={{ marginBottom: 20 }}>
                <button
                    onClick={onBack}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#6c757d', fontSize: 14, padding: '4px 0',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                >
                    <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 14 }} />
                    Назад до галереї
                </button>
            </div>

            {/* ── Hero блок: відео або перше фото ── */}
            <div style={{ marginBottom: 24 }}>
                {videos.length > 0 ? (
                    /* Є відео — показуємо перше (або всі якщо кілька) */
                    <div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: videos.length === 1
                                ? '1fr'
                                : 'repeat(auto-fill, minmax(360px, 1fr))',
                            gap: 16,
                        }}>
                            {videos.map(function(video, idx) {
                                var ytId = getYoutubeId(video.youtube_url);
                                return (
                                    <div key={video.id}>
                                        <div style={{ aspectRatio: '16/9', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
                                            {ytId ? (
                                                <iframe
                                                    ref={function(el) { videoRefsMap.current[video.id] = el; }}
                                                    src={'https://www.youtube.com/embed/' + ytId + '?rel=0&modestbranding=1'}
                                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                                    allow="encrypted-media; fullscreen"
                                                    allowFullScreen
                                                    loading="lazy"
                                                    title={video.caption || 'Відео ' + (idx + 1)}
                                                />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <FontAwesomeIcon icon={faYoutube} style={{ fontSize: 48, color: '#ff4444' }} />
                                                </div>
                                            )}
                                        </div>
                                        {video.caption && (
                                            <div style={{ fontSize: 13, color: '#6c757d', marginTop: 6, paddingLeft: 2 }}>
                                                {video.caption}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : photos.length > 0 ? (
                    /* Немає відео — перше фото як hero */
                    <div
                        onClick={function() { openLightbox(0); }}
                        style={{
                            cursor: 'pointer', borderRadius: 12, overflow: 'hidden',
                            background: '#000', position: 'relative',
                            maxHeight: 480,
                        }}
                    >
                        <img
                            src={constr_uri('docfile?doc_id=' + album.id + '&file_id=' + photos[0].id)}
                            alt={photos[0].caption || album.name}
                            style={{
                                width: '100%', maxHeight: 480,
                                objectFit: 'cover', display: 'block',
                                transition: 'transform 0.3s',
                            }}
                            onMouseEnter={function(e) { e.target.style.transform = 'scale(1.02)'; }}
                            onMouseLeave={function(e) { e.target.style.transform = 'scale(1)'; }}
                        />
                        {/* Overlay з підписом і підказкою */}
                        <div style={{
                            position: 'absolute', bottom: 0, left: 0, right: 0,
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                            padding: '30px 18px 14px',
                            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                        }}>
                            <span style={{ fontSize: 13, color: '#fff', opacity: 0.9 }}>
                                {photos[0].caption || ''}
                            </span>
                            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', gap: 5 }}>
                                <FontAwesomeIcon icon={faExpand} style={{ fontSize: 13 }} />
                                Переглянути
                            </span>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* ── Мета альбому ── */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                    <h2 style={{ fontSize: 24, fontWeight: 500, margin: 0, flex: 1 }}>
                        {album.name}
                    </h2>
                    {cat && (
                        <span style={{
                            background: cat.bg, color: cat.color,
                            padding: '3px 12px', borderRadius: 20,
                            fontSize: 13, fontWeight: 500, flexShrink: 0,
                        }}>
                            {cat.label}
                        </span>
                    )}
                </div>

                {album.event_date && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6c757d', fontSize: 14, marginBottom: 8 }}>
                        <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: 13 }} />
                        {formatDate(album.event_date)}
                    </div>
                )}

                <div style={{ fontSize: 13, color: '#adb5bd', marginBottom: album.description ? 14 : 0 }}>
                    {photos.length > 0 && photos.length + ' фото'}
                    {photos.length > 0 && videos.length > 0 && ' · '}
                    {videos.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <FontAwesomeIcon icon={faYoutube} style={{ color: '#ff4444', fontSize: 12 }} />
                            {videos.length + ' відео'}
                        </span>
                    )}
                </div>

                {/* Опис — тепер після відео/hero */}
                {album.description && (
                    <div
                        style={{
                            fontSize: 14, lineHeight: 1.75, color: '#495057',
                            background: '#f8f9fa', borderRadius: 10,
                            padding: '14px 18px', borderLeft: '3px solid #dee2e6',
                        }}
                        dangerouslySetInnerHTML={{ __html: album.description }}
                    />
                )}
            </div>

            {/* Фото — сітка з пагінацією */}
            {photos.length > 0 && (
                <div>
                    {/* Якщо немає відео — перше фото вже показано як hero, починаємо з другого */}
                    {(videos.length > 0 || photos.length > 1) && (
                        <div style={{ fontSize: 14, fontWeight: 500, color: '#495057', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FontAwesomeIcon icon={faImage} />
                            Фото
                        </div>
                    )}

                    {/* Сітка */}
                    {(() => {
                        // Якщо немає відео — перше фото вже є hero, пропускаємо його
                        var gridPhotos = videos.length === 0 ? pagedPhotos.slice(1) : pagedPhotos;
                        if (gridPhotos.length === 0) return null;
                        return (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: 10,
                                marginBottom: 20,
                            }}>
                                {gridPhotos.map(function(photo, idx) {
                                    // Реальний індекс для лайтбоксу
                                    var lbIdx = videos.length === 0 ? idx + 1 : idx;
                                    return (
                                        <div
                                            key={photo.id}
                                            onClick={function() { openLightbox(lbIdx); }}
                                            style={{
                                                borderRadius: 8, overflow: 'hidden',
                                                cursor: 'pointer', background: '#f0f0f0',
                                                position: 'relative',
                                            }}
                                            onMouseEnter={function(e) { e.currentTarget.style.opacity = '0.9'; }}
                                            onMouseLeave={function(e) { e.currentTarget.style.opacity = '1'; }}
                                        >
                                            <div style={{ aspectRatio: '4/3', overflow: 'hidden' }}>
                                                <img
                                                    src={constr_uri('docfile?doc_id=' + album.id + '&file_id=' + photo.id)}
                                                    alt={photo.caption || ''}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s' }}
                                                    loading="lazy"
                                                    onMouseEnter={function(e) { e.target.style.transform = 'scale(1.04)'; }}
                                                    onMouseLeave={function(e) { e.target.style.transform = 'scale(1)'; }}
                                                />
                                            </div>
                                            {photo.caption && (
                                                <div style={{
                                                    position: 'absolute', bottom: 0, left: 0, right: 0,
                                                    background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
                                                    padding: '16px 8px 6px',
                                                    fontSize: 12, color: '#fff',
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {photo.caption}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}

                    {/* Пагінація */}
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <button
                                onClick={function() { setPage(function(p) { return Math.max(1, p - 1); }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                disabled={page === 1}
                                style={{
                                    padding: '6px 14px', borderRadius: 8, fontSize: 13,
                                    border: '1px solid #dee2e6', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer',
                                    color: page === 1 ? '#adb5bd' : '#495057',
                                    display: 'flex', alignItems: 'center', gap: 5,
                                }}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 11 }} />
                                Назад
                            </button>

                            {Array.from({ length: totalPages }, function(_, i) { return i + 1; }).map(function(p) {
                                var isActive = p === page;
                                return (
                                    <button
                                        key={p}
                                        onClick={function() { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                        style={{
                                            width: 36, height: 36, borderRadius: 8, fontSize: 13,
                                            border: '1px solid ' + (isActive ? '#0d6efd' : '#dee2e6'),
                                            background: isActive ? '#0d6efd' : '#fff',
                                            color: isActive ? '#fff' : '#495057',
                                            cursor: 'pointer', fontWeight: isActive ? 500 : 400,
                                        }}
                                    >
                                        {p}
                                    </button>
                                );
                            })}

                            <button
                                onClick={function() { setPage(function(p) { return Math.min(totalPages, p + 1); }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                disabled={page === totalPages}
                                style={{
                                    padding: '6px 14px', borderRadius: 8, fontSize: 13,
                                    border: '1px solid #dee2e6', background: '#fff',
                                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                                    color: page === totalPages ? '#adb5bd' : '#495057',
                                    display: 'flex', alignItems: 'center', gap: 5,
                                }}
                            >
                                Далі
                                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11 }} />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Лайтбокс */}
            {lightbox && (
                <Lightbox
                    items={lightbox.items}
                    startIndex={lightbox.startIndex}
                    docId={album.id}
                    onClose={function() { setLightbox(null); }}
                />
            )}
        </div>
    );
};


// ══════════════════════════════════════════════════════════════════════════════
// СПИСОК АЛЬБОМІВ
// ══════════════════════════════════════════════════════════════════════════════

const AlbumCard = function({ album, onClick }) {
    var cat = CATEGORY_META[album.category] || null;
    var hasCover = !!album.cover;

    return (
        <div
            onClick={function() { onClick(album.id); }}
            style={{
                border: '1px solid #dee2e6', borderRadius: 12,
                overflow: 'hidden', background: '#fff', cursor: 'pointer',
                transition: 'box-shadow 0.15s, transform 0.15s',
            }}
            onMouseEnter={function(e) {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={function(e) {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
            }}
        >
            {/* Обкладинка */}
            <div style={{ width: '100%', aspectRatio: '16/9', background: '#f0f0f0', overflow: 'hidden', position: 'relative' }}>
                {hasCover ? (
                    <img
                        src={constr_uri('docfile?doc_id=' + album.id + '&file_id=' + album.cover.file_id)}
                        alt={album.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                        loading="lazy"
                        onMouseEnter={function(e) { e.target.style.transform = 'scale(1.04)'; }}
                        onMouseLeave={function(e) { e.target.style.transform = 'scale(1)'; }}
                    />
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#adb5bd' }}>
                        <FontAwesomeIcon icon={faImage} style={{ fontSize: 32, opacity: 0.4 }} />
                        <span style={{ fontSize: 13 }}>{album.files_count || 0} медіа</span>
                    </div>
                )}

                {/* Категорія поверх обкладинки */}
                {cat && (
                    <div style={{
                        position: 'absolute', top: 8, left: 8,
                        background: cat.bg, color: cat.color,
                        padding: '2px 9px', borderRadius: 20,
                        fontSize: 11, fontWeight: 500,
                    }}>
                        {cat.label}
                    </div>
                )}

                {/* Лічильник */}
                {hasCover && album.files_count > 0 && (
                    <div style={{
                        position: 'absolute', bottom: 8, right: 8,
                        background: 'rgba(0,0,0,0.55)', borderRadius: 5,
                        padding: '2px 8px', fontSize: 11, color: '#fff',
                    }}>
                        {album.files_count} медіа
                    </div>
                )}
            </div>

            {/* Інфо */}
            <div style={{ padding: '12px 14px' }}>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {album.name}
                </div>
                {album.event_date && (
                    <div style={{ fontSize: 12, color: '#6c757d', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: 11 }} />
                        {formatDateShort(album.event_date)}
                    </div>
                )}
            </div>
        </div>
    );
};


// ══════════════════════════════════════════════════════════════════════════════
// ГОЛОВНИЙ КОМПОНЕНТ
// ══════════════════════════════════════════════════════════════════════════════

const MediaGalleryPublic = function({ kgId, groups, groupId }) {
    const [albums, setAlbums]       = useState([]);
    const [loading, setLoading]     = useState(true);
    const [page, setPage]           = useState(1);
    const [openAlbumId, setOpenAlbumId] = useState(null);
    const [filterCategory, setFilterCategory] = useState('');

    useEffect(function() {
        if (!kgId) return;
        setLoading(true);
        listPublicAlbums(kgId, { groupId: groupId || undefined })
            .then(function(data) {
                setAlbums(Array.isArray(data) ? data : []);
            })
            .catch(function() { setAlbums([]); })
            .finally(function() { setLoading(false); });
    }, [kgId, groupId]);

    // Якщо відкритий альбом — показуємо AlbumView
    if (openAlbumId) {
        return (
            <AlbumView
                albumId={openAlbumId}
                onBack={function() { setOpenAlbumId(null); }}
            />
        );
    }

    // Фільтрація по категорії (клієнтська)
    var filtered = filterCategory
        ? albums.filter(function(a) { return a.category === filterCategory; })
        : albums;

    // Пагінація
    var totalPages   = Math.ceil(filtered.length / PAGE_SIZE);
    var pagedAlbums  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Унікальні категорії для фільтру
    var usedCategories = albums
        .map(function(a) { return a.category; })
        .filter(function(c, i, arr) { return c && arr.indexOf(c) === i; });

    if (loading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" size="sm" variant="primary" />
            </div>
        );
    }

    if (albums.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#adb5bd' }}>
                <FontAwesomeIcon icon={faImage} style={{ fontSize: 36, marginBottom: 12, display: 'block', margin: '0 auto 12px', opacity: 0.3 }} />
                <div style={{ fontSize: 15 }}>Галерея ще порожня</div>
            </div>
        );
    }

    return (
        <div>
            {/* Фільтр по категорії */}
            {usedCategories.length > 1 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                    <button
                        onClick={function() { setFilterCategory(''); setPage(1); }}
                        style={{
                            padding: '4px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                            border: '1px solid ' + (!filterCategory ? '#85b7eb' : '#ced4da'),
                            background: !filterCategory ? '#e6f1fb' : '#fff',
                            color: !filterCategory ? '#0c447c' : '#6c757d',
                        }}
                    >
                        Всі
                    </button>
                    {usedCategories.map(function(cat) {
                        var meta = CATEGORY_META[cat];
                        if (!meta) return null;
                        var sel = filterCategory === cat;
                        return (
                            <button
                                key={cat}
                                onClick={function() { setFilterCategory(cat); setPage(1); }}
                                style={{
                                    padding: '4px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                                    border: '1px solid ' + (sel ? meta.color : '#ced4da'),
                                    background: sel ? meta.bg : '#fff',
                                    color: sel ? meta.color : '#6c757d',
                                    fontWeight: sel ? 500 : 400,
                                }}
                            >
                                {meta.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Сітка альбомів */}
            {pagedAlbums.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#adb5bd', fontSize: 14 }}>
                    Немає альбомів у цій категорії
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 16,
                    marginBottom: 20,
                }}>
                    {pagedAlbums.map(function(album) {
                        return (
                            <AlbumCard
                                key={album.id}
                                album={album}
                                onClick={setOpenAlbumId}
                            />
                        );
                    })}
                </div>
            )}

            {/* Пагінація */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <button
                        onClick={function() { setPage(function(p) { return Math.max(1, p - 1); }); }}
                        disabled={page === 1}
                        style={{
                            padding: '6px 14px', borderRadius: 8, fontSize: 13,
                            border: '1px solid #dee2e6', background: '#fff',
                            cursor: page === 1 ? 'not-allowed' : 'pointer',
                            color: page === 1 ? '#adb5bd' : '#495057',
                            display: 'flex', alignItems: 'center', gap: 5,
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 11 }} />
                        Назад
                    </button>

                    {Array.from({ length: totalPages }, function(_, i) { return i + 1; }).map(function(p) {
                        var isActive = p === page;
                        return (
                            <button
                                key={p}
                                onClick={function() { setPage(p); }}
                                style={{
                                    width: 36, height: 36, borderRadius: 8, fontSize: 13,
                                    border: '1px solid ' + (isActive ? '#0d6efd' : '#dee2e6'),
                                    background: isActive ? '#0d6efd' : '#fff',
                                    color: isActive ? '#fff' : '#495057',
                                    cursor: 'pointer', fontWeight: isActive ? 500 : 400,
                                }}
                            >
                                {p}
                            </button>
                        );
                    })}

                    <button
                        onClick={function() { setPage(function(p) { return Math.min(totalPages, p + 1); }); }}
                        disabled={page === totalPages}
                        style={{
                            padding: '6px 14px', borderRadius: 8, fontSize: 13,
                            border: '1px solid #dee2e6', background: '#fff',
                            cursor: page === totalPages ? 'not-allowed' : 'pointer',
                            color: page === totalPages ? '#adb5bd' : '#495057',
                            display: 'flex', alignItems: 'center', gap: 5,
                        }}
                    >
                        Далі
                        <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11 }} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default MediaGalleryPublic;